import moment from 'moment'

import { AuthAPI } from 'src/api'
import $router from 'src/router/index'
import $store from 'src/store'
import { LocalStorageService } from 'src/services'
import { waitUntil } from 'src/utils/common.js'

import { shutdown as IntercomShutdown, boot as IntercomBoot } from '@intercom/messenger-js-sdk'

export class AuthService {
  static ACCESS_TOKEN_KEY = 'accessToken'
  static REFRESH_TOKEN_KEY = 'refreshToken'
  static WAIT_UNTIL_INTERVAL = 50
  static IS_REFRESHING_KEY = 'tokenIsRefreshing'
  static IS_REFRESHING_TIMEOUT = 15
  static REFRESH_MAX_WAIT_TIME = 5000

  static get isRefreshing() {
    const refreshStartTime = LocalStorageService.get(this.IS_REFRESHING_KEY)
    if (!refreshStartTime) {
      return false
    }

    const elapsedTime = moment().diff(moment(refreshStartTime), 'seconds')
    return elapsedTime <= this.IS_REFRESHING_TIMEOUT
  }

  static set isRefreshing(value) {
    if (value) {
      LocalStorageService.set(this.IS_REFRESHING_KEY, moment().toISOString())
    } else {
      LocalStorageService.remove(this.IS_REFRESHING_KEY)
    }
  }

  static get accessToken() {
    return LocalStorageService.get(this.ACCESS_TOKEN_KEY)
  }

  static set accessToken({ token, expiresAt }) {
    LocalStorageService.set(this.ACCESS_TOKEN_KEY, { token, expiresAt })
  }

  static get hasAccessToken() {
    if (!LocalStorageService.has(this.ACCESS_TOKEN_KEY)) {
      return false
    }

    const { expiresAt } = LocalStorageService.get(this.ACCESS_TOKEN_KEY)
    const isNotExpired = moment().unix() < expiresAt
    return isNotExpired
  }

  static get refreshToken() {
    return LocalStorageService.get(this.REFRESH_TOKEN_KEY)
  }

  static set refreshToken(token) {
    LocalStorageService.set(this.REFRESH_TOKEN_KEY, token)
  }

  static get hasRefreshToken() {
    return LocalStorageService.has(this.REFRESH_TOKEN_KEY)
  }

  static setTokens({ accessToken, expiresAt, refreshToken }) {
    this.accessToken = { token: accessToken, expiresAt }
    this.refreshToken = refreshToken
  }

  static waitForRefreshingEnd() {
    return waitUntil(() => !this.isRefreshing, this.WAIT_UNTIL_INTERVAL)
  }

  static async handleAccessTokenRefresh() {
    if (this.hasAccessToken) {
      return
    }

    if (this.isRefreshing) {
      await this.waitForRefreshingEnd()
      return
    }

    if (!this.hasRefreshToken) {
      await this.logout()
      throw new Error('No refresh token available')
    }

    try {
      this.isRefreshing = true
      const data = await AuthAPI.refreshToken({
        refreshToken: this.refreshToken,
      })
      this.setTokens(data)
    } catch {
      try {
        await waitUntil(
          () => this.hasAccessToken,
          this.WAIT_UNTIL_INTERVAL,
          this.REFRESH_MAX_WAIT_TIME
        )
      } catch {
        await this.logout()
        throw new Error('Failed to refresh access token')
      }
    } finally {
      this.isRefreshing = false
    }
  }

  static async logout({ shouldRedirect = true } = {}) {
    LocalStorageService.remove(this.ACCESS_TOKEN_KEY)
    LocalStorageService.remove(this.REFRESH_TOKEN_KEY)
    this.isRefreshing = false
    $store.dispatch('clearSettings')
    if (shouldRedirect) {
      await $router.replace({ name: 'login' })
    }
    IntercomShutdown()
    setTimeout(() => {
      IntercomBoot({ app_id: process.env.VUE_APP_INTERCOM_APP_ID })
    }, 1000)
  }
}
